
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Progress } from "@/components/ui/progress";
import { useNetworkStatus } from '@/contexts/NetworkStatusContext';
import { Home, RefreshCw, Check, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

type SyncTarget = 'jimatbiz' | 'jimatshop' | 'pos';
type SyncStatus = 'idle' | 'syncing' | 'completed' | 'failed';

interface SyncState {
  status: SyncStatus;
  progress: number;
  lastSync: string | null;
  error: string | null;
}

const SyncProgress = () => {
  const [syncStates, setSyncStates] = useState<Record<SyncTarget, SyncState>>({
    jimatbiz: { status: 'idle', progress: 0, lastSync: null, error: null },
    jimatshop: { status: 'idle', progress: 0, lastSync: null, error: null },
    pos: { status: 'idle', progress: 0, lastSync: null, error: null }
  });
  const { toast } = useToast();
  const { isOnline } = useNetworkStatus();

  useEffect(() => {
    // Auto-start sync with Jimatbiz and JimatShop if online
    if (isOnline) {
      const syncTargets: SyncTarget[] = ['jimatbiz', 'jimatshop'];
      syncTargets.forEach(target => {
        if (syncStates[target].status === 'idle') {
          startSync(target);
        }
      });
    }
  }, [isOnline]);

  const startSync = async (target: SyncTarget) => {
    if (!isOnline) {
      toast({
        title: "Network Connection Required",
        description: "Please connect to the internet to sync data.",
        variant: "destructive",
      });
      return;
    }

    setSyncStates(prev => ({
      ...prev,
      [target]: { ...prev[target], status: 'syncing', progress: 0, error: null }
    }));

    // Simulate sync progress with intervals
    const interval = setInterval(() => {
      setSyncStates(prev => {
        const current = prev[target];
        
        if (current.status !== 'syncing') {
          clearInterval(interval);
          return prev;
        }
        
        const newProgress = current.progress + 10;
        
        if (newProgress >= 100) {
          clearInterval(interval);
          
          // Show toast notification
          toast({
            title: `Sync Complete: ${target}`,
            description: `All data has been synchronized with ${target}.`,
          });
          
          // Update state to completed
          return {
            ...prev,
            [target]: { 
              ...current, 
              status: 'completed', 
              progress: 100,
              lastSync: new Date().toISOString()
            }
          };
        }
        
        return {
          ...prev,
          [target]: { ...current, progress: newProgress }
        };
      });
    }, 500);

    // Register sync with service worker (fixed TypeScript error)
    try {
      if ('serviceWorker' in navigator && 'SyncManager' in window) {
        const registration = await navigator.serviceWorker.ready;
        // Type safety check - cast to any to avoid TypeScript error
        const syncManager = (registration as any).sync;
        if (syncManager && typeof syncManager.register === 'function') {
          await syncManager.register(`sync-${target}`);
          console.log(`Sync registered successfully for ${target}`);
        } else {
          console.warn('Background sync not supported');
        }
      }
    } catch (error) {
      console.error(`Failed to register sync for ${target}:`, error);
      setSyncStates(prev => ({
        ...prev,
        [target]: { 
          ...prev[target], 
          status: 'failed', 
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }));
    }

    return () => clearInterval(interval);
  };

  const getSyncStatusText = (state: SyncState) => {
    switch (state.status) {
      case 'idle': return 'Ready to sync';
      case 'syncing': return `Syncing... ${state.progress}%`;
      case 'completed': return 'Sync completed';
      case 'failed': return 'Sync failed';
      default: return 'Unknown status';
    }
  };

  const formatLastSyncTime = (timestamp: string | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const renderSyncCard = (target: SyncTarget, title: string, description: string) => {
    const state = syncStates[target];
    
    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>{title}</span>
            {state.status === 'completed' && <Check className="text-green-500 h-5 w-5" />}
            {state.status === 'failed' && <AlertCircle className="text-red-500 h-5 w-5" />}
          </CardTitle>
          <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Progress value={state.progress} className="mb-2" />
            <p className="text-sm">{getSyncStatusText(state)}</p>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              Last sync: {formatLastSyncTime(state.lastSync)}
            </span>
            <Button 
              onClick={() => startSync(target)} 
              disabled={state.status === 'syncing'}
              size="sm"
            >
              {state.status === 'syncing' ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync Now
                </>
              )}
            </Button>
          </div>
          {state.error && (
            <p className="mt-2 text-sm text-red-500">Error: {state.error}</p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Synchronization Center</h1>
          <p className="text-muted-foreground">
            Keep your data in sync between POS, Jimatbiz B2B platform, and Jimatshop consumer store
          </p>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Link to="/dashboard/pos-v2" className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-muted">
                <Home className="h-5 w-5" />
              </Link>
            </TooltipTrigger>
            <TooltipContent>
              <p>Home</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          {renderSyncCard(
            'jimatbiz',
            'Jimatbiz B2B Platform',
            'Sync inventory, orders, and pricing with your B2B distribution platform'
          )}
        </div>
        <div>
          {renderSyncCard(
            'jimatshop',
            'Jimatshop Consumer Store',
            'Sync products, inventory, and promotions with your online store'
          )}
        </div>
      </div>

      <div className="mt-6">
        {renderSyncCard(
          'pos',
          'Local POS System',
          'Sync local POS data with central database'
        )}
      </div>

      <div className="mt-6 p-4 bg-muted rounded-md">
        <h3 className="text-sm font-medium mb-2">Sync Status</h3>
        <p className="text-sm text-muted-foreground">
          Network Status: {isOnline ? 'Online' : 'Offline'}
        </p>
        <p className="text-sm text-muted-foreground">
          Next Scheduled Sync: {new Date(Date.now() + 3600000).toLocaleString()}
        </p>
      </div>
    </div>
  );
};

export default SyncProgress;
